import { useQuery } from '@tanstack/react-query'

/**
 * Parallel entity search for the command palette.
 *
 * Fires 5 small list requests with `q=<term>` and `pageSize=5` and
 * returns the merged top hits per entity kind. React Query handles
 * caching + deduplication; the consumer should debounce the input
 * before passing the term in.
 *
 * Returns an empty payload when `term` is shorter than 2 chars so we
 * don't hammer the backend on every keystroke.
 */
export interface CommandSearchHit {
  id: string
  label: string
  sublabel?: string
  href: string
}

export interface CommandSearchResults {
  customers: CommandSearchHit[]
  boats: CommandSearchHit[]
  bookings: CommandSearchHit[]
  invoices: CommandSearchHit[]
  quotes: CommandSearchHit[]
}

const EMPTY: CommandSearchResults = {
  customers: [],
  boats: [],
  bookings: [],
  invoices: [],
  quotes: [],
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

interface Item {
  id: string
}
interface CustomerItem extends Item {
  firstName: string
  lastName: string
  email?: string | null
  category?: string | null
}
interface BoatItem extends Item {
  name: string
  type?: string | null
  customer?: { firstName: string; lastName: string } | null
}
interface BookingItem extends Item {
  code: string
  status: string
  customer?: { firstName: string; lastName: string } | null
  boat?: { name: string } | null
}
interface InvoiceItem extends Item {
  code: string
  status: string
  totalIdr?: string | number
  customer?: { firstName: string; lastName: string } | null
}
interface QuoteItem extends Item {
  code: string
  status: string
  customer?: { firstName: string; lastName: string } | null
}

export function useCommandSearch(term: string) {
  const enabled = term.trim().length >= 2
  return useQuery({
    queryKey: ['command-search', term],
    queryFn: async (): Promise<CommandSearchResults> => {
      const q = encodeURIComponent(term)
      const [customers, boats, bookings, invoices, quotes] = await Promise.all([
        fetchJson<{ items: CustomerItem[] }>(`/api/customers?q=${q}&pageSize=5`).catch(() => ({ items: [] })),
        fetchJson<{ items: BoatItem[] }>(`/api/boats?q=${q}&pageSize=5`).catch(() => ({ items: [] })),
        fetchJson<{ items: BookingItem[] }>(`/api/bookings?q=${q}&pageSize=5`).catch(() => ({ items: [] })),
        fetchJson<{ items: InvoiceItem[] }>(`/api/invoices?q=${q}&pageSize=5`).catch(() => ({ items: [] })),
        fetchJson<{ items: QuoteItem[] }>(`/api/quotes?q=${q}&pageSize=5`).catch(() => ({ items: [] })),
      ])
      return {
        customers: customers.items.map((c) => ({
          id: c.id,
          label: `${c.firstName} ${c.lastName}`.trim(),
          sublabel: c.email ?? c.category ?? undefined,
          href: `/dashboard/accounts/customers/${c.id}`,
        })),
        boats: boats.items.map((b) => ({
          id: b.id,
          label: b.name,
          sublabel: [b.type, b.customer && `${b.customer.firstName} ${b.customer.lastName}`]
            .filter(Boolean)
            .join(' · '),
          href: `/dashboard/accounts/boats/${b.id}`,
        })),
        bookings: bookings.items.map((b) => ({
          id: b.id,
          label: b.code,
          sublabel: [b.customer && `${b.customer.firstName} ${b.customer.lastName}`, b.boat?.name, b.status]
            .filter(Boolean)
            .join(' · '),
          href: `/dashboard/reservations/${b.id}`,
        })),
        invoices: invoices.items.map((i) => ({
          id: i.id,
          label: i.code,
          sublabel: [i.customer && `${i.customer.firstName} ${i.customer.lastName}`, i.status]
            .filter(Boolean)
            .join(' · '),
          href: `/dashboard/billing/invoices/${i.id}`,
        })),
        quotes: quotes.items.map((q2) => ({
          id: q2.id,
          label: q2.code,
          sublabel: [q2.customer && `${q2.customer.firstName} ${q2.customer.lastName}`, q2.status]
            .filter(Boolean)
            .join(' · '),
          href: `/dashboard/billing/quotes/${q2.id}`,
        })),
      }
    },
    enabled,
    initialData: EMPTY,
    staleTime: 30_000,
  })
}
